// ---------------------------------------------------------------------------
// Perspektive (Roadmap AP-41, PF 26 und PF 50) – was der Text über die Perspektive einer Szene sagt, über PF 56 hinaus
// (settingConflicts.ts: gesetzt, aber nur Gedanken anderer; Head-Hopping):
//  - Perspektive aus dem Text (PF 50): keine gesetzt, und der Draft gibt Gedanken genau einer Person wieder → sie wird vorgeschlagen,
//    am ersten ihrer Gedanken. ✕ merkt „ohne Perspektive“ im Dokument. In der Ich-Form (ohne solche Gedanken) der Ich-Erzähler: wer in den
//    anderen Szenen in der Ich-Form die Perspektive hat – am ersten „ich“ der Erzählung.
//  - Perspektivfigur nicht in der Szene: gesetzt, aber weder beteiligt noch in einem Text der Szene genannt (verknüpft oder nur mit Namen),
//    ohne eigene Rede oder Gedanken. Erst, wenn es etwas zu prüfen gibt (Beteiligte bzw. Text); in der Ich-Form ist der Erzähler „ich“.
//  - Szene ohne Perspektive: geschrieben, aber keine gesetzt – nur ein Hinweis, und nur, wenn die Geschichte überhaupt mit Perspektiven
//    arbeitet (eine Szene hat eine).
// ---------------------------------------------------------------------------

import type { StoryCtx } from './ctx.js';
import { textsOf } from './autoInstances.js';
import { isKnower } from './knowledge.js';
import { draftDocOf, mentionedEntities, participantsOf, povOf } from './narrative.js';
import { plainRefs, refIndex } from './refs.js';
import { isScene } from './scene.js';
import type { ThoughtLink } from './settingConflicts.js';
import { spansIn } from './spans.js';
import { unlinkedTerms, type TermIndex } from './terms.js';
import type { Entity, IgnoredTerm, StoryNode } from './types.js';

const keyOf = (ctx: StoryCtx, id: string): string => ctx.entities.get(id)?.refKey ?? id;

/** Vorschlag „Perspektive aus dem Text“ an seiner Stelle `at` (dem ersten Gedanken der Person) */
export interface PovSuggestion<T extends ThoughtLink> {
  at: T;
  entityId: string;
}

/** „Ohne Perspektive“, gemerkt im Dokument: diese Person nicht mehr vorschlagen (und die Szene nicht als „ohne Perspektive“ melden) */
export const povSuggestionIgnore = (ctx: StoryCtx, s: { entityId: string }): IgnoredTerm => ({ term: '~ohne-perspektive', before: '', after: `@${keyOf(ctx, s.entityId)}` });

/** Perspektive aus dem Text (PF 50): keine gesetzt, Gedanken (`thoughts`, in Textreihenfolge) nur einer Person → sie; sonst null */
export function povFromText<T extends ThoughtLink>(ctx: StoryCtx, scene: StoryNode, thoughts: readonly T[], ignored?: readonly IgnoredTerm[]): PovSuggestion<T> | null {
  if (povOf(ctx, scene)) return null;
  const thinking = thoughts.map((t) => ({ t, ids: t.entityIds.filter((id) => isKnower(ctx, id)) })).filter((x) => x.ids.length > 0);
  const persons = new Set(thinking.flatMap((x) => x.ids));
  if (persons.size !== 1) return null;
  const s = { at: thinking[0]!.t, entityId: [...persons][0]! };
  const entry = povSuggestionIgnore(ctx, s);
  return ignored?.some((x) => x.term === entry.term && x.after === entry.after) ? null : s;
}

/** Hat das Dokument „ohne Perspektive“ gemerkt? */
export const withoutPov = (ignored: readonly IgnoredTerm[] | undefined): boolean => !!ignored?.some((x) => x.term === '~ohne-perspektive');

const SPEECH = /„[^“”]*[“”]|»[^«]*«|«[^»]*»|"[^"]*"/g;
const FIRST_PERSON = /(?<![\p{L}\p{N}])(?:ich|mich|mir|mein(?:e|en|em|er|es)?)(?![\p{L}\p{N}])/giu;

/** In der Ich-Form erzählt: „ich“ (mich, mir, mein …) mindestens zweimal außerhalb von Rede, Gedanken und Anführungszeichen */
export function isFirstPerson(markdown: string): boolean {
  let text = markdown;
  // Rede und Gedanken (Bezüge) zuerst heraus, von hinten, damit die Lagen stimmen
  for (const s of spansIn(markdown).reverse()) if (s.role === 'rede' || s.role === 'gedanke') text = text.slice(0, s.from) + ' ' + text.slice(s.to);
  const narration = plainRefs(text).replace(SPEECH, ' ');
  return (narration.match(FIRST_PERSON)?.length ?? 0) >= 2;
}

/** Die erste Stelle der Ich-Form in einem Text ohne Rede, Gedanken und Anführungszeichen (die schon durch Leerzeichen ersetzt sind) */
export function firstPersonIn(narration: string): { from: number; to: number } | null {
  const m = new RegExp(FIRST_PERSON.source, 'iu').exec(narration.replace(SPEECH, (s) => ' '.repeat(s.length)));
  return m ? { from: m.index, to: m.index + m[0].length } : null;
}

const narratorCache = new WeakMap<StoryCtx, Map<string, number>>();

/**
 * Ich-Erzähler einer Szene ohne Perspektive (PF 50), deren Draft in der Ich-Form erzählt ist: die Person, die in den übrigen Szenen in der
 * Ich-Form am häufigsten die Perspektive hat; bei Gleichstand bzw. ohne solche Szenen keiner. Nicht, wenn sie als „ohne Perspektive“
 * gemerkt ist.
 */
export function firstPersonNarrator(ctx: StoryCtx, scene: StoryNode, ignored?: readonly IgnoredTerm[]): string | null {
  if (povOf(ctx, scene)) return null;
  const draft = draftDocOf(scene);
  if (!draft || !isFirstPerson(draft.text)) return null;
  let counts = narratorCache.get(ctx);
  if (!counts) {
    counts = new Map();
    for (const n of ctx.story.nodes) {
      if (!isScene(n)) continue;
      const pov = povOf(ctx, n);
      const text = draftDocOf(n)?.text;
      if (pov && text && isFirstPerson(text)) counts.set(pov, (counts.get(pov) ?? 0) + 1);
    }
    narratorCache.set(ctx, counts);
  }
  const ranked = [...counts].sort((a, b) => b[1] - a[1]);
  if (ranked.length === 0 || (ranked.length > 1 && ranked[0]![1] === ranked[1]![1])) return null;
  const entityId = ranked[0]![0];
  const entry = povSuggestionIgnore(ctx, { entityId });
  return ignored?.some((x) => x.term === entry.term && x.after === entry.after) ? null : entityId;
}

/** „Kommt in der Szene vor“ ist gemerkt (Perspektivfigur fehlt, ist aber Absicht) */
export const povAbsentIgnore = (ctx: StoryCtx, povId: string): IgnoredTerm => ({ term: '~abwesend', before: `@${keyOf(ctx, povId)}`, after: '' });

/**
 * Perspektivfigur nicht in der Szene: gesetzt, aber weder beteiligt noch in einem Text der Szene genannt (Verweis, Name ohne Verweis, Rede,
 * Gedanke …). Nur, wenn es etwas zu prüfen gibt – Beteiligte oder Text –, nicht in der Ich-Form und nicht, wenn es im Draft als Absicht
 * gemerkt ist. Gibt der Draft Gedanken anderer wieder, ist das „Perspektive gegen Text“ (PF 56) – gemeldet bzw. dort als Absicht gemerkt. `terms` (Namen und Vorkommen, `termIndex`) und `refs` (`refIndex`) reicht der Aufrufer, der sie für alle Szenen hat.
 */
export function povAbsent(ctx: StoryCtx, scene: StoryNode, terms: TermIndex, refs: ReadonlyMap<string, Entity> = refIndex(ctx)): boolean {
  const pov = povOf(ctx, scene);
  if (!pov || !isScene(scene)) return false;
  const draft = draftDocOf(scene);
  const ignore = povAbsentIgnore(ctx, pov);
  if (draft?.ignored?.some((x) => x.term === ignore.term && x.before === ignore.before)) return false;
  const present = participantsOf(ctx, scene.id);
  const written = scene.documents.some((d) => d.text.trim() !== '');
  if (present.has(pov) || (present.size === 0 && !written)) return false;
  if (mentionedEntities(ctx, scene).has(pov)) return false;
  if (textsOf(scene).some((t) => spansIn(t).some((s) => s.keys.some((k) => refs.get(k)?.id === pov)))) return false;
  if (draft && spansIn(draft.text).some((s) => s.role === 'gedanke' && s.keys.some((k) => refs.has(k)))) return false;
  if (draft && isFirstPerson(draft.text)) return false;
  return !scene.documents.some((d) => d.text.trim() !== '' && unlinkedTerms(d, terms).some((h) => h.entityIds.includes(pov)));
}

/** Arbeitet die Geschichte mit Perspektiven (hat eine Szene eine)? Erst dann ist „ohne Perspektive“ einen Hinweis wert. */
export const usesPov = (ctx: StoryCtx): boolean => ctx.story.nodes.some((n) => isScene(n) && povOf(ctx, n) !== null);
